import React, { FC, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Person } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';

type Props = {
  isOpen: boolean;
  agentId: number;
  onClosed: () => void;
  onDelete: (agent: any) => void;
};
const DeleteAgentModal: FC<Props> = ({
  isOpen,
  agentId,
  onClosed,
  onDelete,
}) => {
  const proxyParameters = useProxyParameters();
  const { formatMessage, locale } = useIntl();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const { data, error, status } = useConfirmApi<any>({
    url: `agents/${agentId}`,
    method: 'GET',
    disabled: !isOpen,
    clearOnChange: true,
    clearOnError: true,
  });

  const handleOnDeleteCallback = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `agents/${agentId}`,
      proxyParameters,
      (data, error: unknown, hardErrorMessage: unknown) => {
        if (error || hardErrorMessage) {
          setErrorMessage(
            getFriendlyErrorMessageIfErrorOccurred(error, hardErrorMessage)
          );
          return;
        }
        onDelete(data);
        onClosed();
      }
    );
  }, [agentId, onClosed, onDelete, proxyParameters]);

  useEffect(() => {
    if (status === 'ERROR') {
      // @ts-expect-error
      if (error?.status === 404) {
        toast.warn(
          formatMessage({
            id: 'app.views.ai.agents.agents_admin.delete_modal.error.agent_not_owned',
            defaultMessage:
              'You do not have permission to delete this agent. It may have already been deleted or you may have lost the ownership, since belongs to people you may not have admin capability against anymore.',
          })
        );
      } else {
        toast.error(
          formatMessage({
            id: 'app.views.ai.agents.agents_admin.delete_modal.error.fetching_agent_data',
            defaultMessage: 'Error fetching agent data',
          })
        );
      }
    }
  }, [error, formatMessage, status]);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.agents_admin.delete_modal.message.fetching_agent_data',
          defaultMessage: 'Fetching agent data',
        })}
      />
    );
  }

  return (
    <>
      {!!data && (
        <ConfirmationDialogModal
          isOpen={isOpen}
          onClosed={onClosed}
          toggle={onClosed}
          confirmCallback={handleOnDeleteCallback}
          title={formatMessage(
            {
              id: 'app.views.ai.agents.agents_admin.delete_modal.title.delete_agent',
              defaultMessage:
                'Delete agent owned by {ownersNames}, created on {creationDate}',
            },
            {
              ownersNames: data.owners
                .map((owner: Person) => owner.full_name)
                .join(', '),
              creationDate: getPrettyDate({
                dateString: data.created_at,
                locale,
              }),
            }
          )}
          description={
            <div>
              <div className="mb-4">
                {formatMessage({
                  id: 'app.views.ai.agents.agents_admin.delete_modal.description.delete_agent',
                  defaultMessage:
                    'Are you sure that you want to remove this agent?',
                })}
              </div>
            </div>
          }
          confirmText={formatMessage({
            id: 'app.views.ai.agents.agents_admin.delete_modal.confirm_text.delete',
            defaultMessage: 'Delete',
          })}
          validationErrors={errorMessage}
        />
      )}
    </>
  );
};

export default React.memo(DeleteAgentModal);
